import { isCurrentDay } from "@calendar/weekDates";

import type { TodayVisit } from "./visitsClient";

/**
 * Pure visit-status predicates, kept free of visitsClient's API-client import
 * chain so grouping helpers and their tests can run without a browser window.
 * Consumers import via visitsClient's re-exports; only pure modules (and
 * tests) need to reach in here directly.
 */

/**
 * Whether a visit is finished. A visit has no status column — it's complete
 * when no stage is active or pending. The backend resolves `currentStage`
 * active → pending → done, so a `done` current stage means every other stage
 * is already done/skipped. Null (no stages, or all skipped) is treated as not
 * complete, so such visits stay in the active list.
 */
export const isVisitComplete = (visit: TodayVisit): boolean =>
  visit.currentStage?.status === "done";

/**
 * Whether a visit was cancelled (the appointment was real but didn't happen).
 * The backend has no status column — cancellation is a `cancelledAt` timestamp.
 */
export const isVisitCancelled = (visit: TodayVisit): boolean =>
  visit.cancelledAt !== null;

/**
 * Whether a visit has settled out of the active flow — completed or cancelled.
 * Both drop into the collapsible "Completed" section of the rail rather than
 * the in-progress list (WEB-3210); the card distinguishes the two visually.
 */
export const isVisitSettled = (visit: TodayVisit): boolean =>
  isVisitCancelled(visit) || isVisitComplete(visit);

/**
 * The visit a `?visit=` selection points at, across every board read the
 * workspace has: today's visits and the still-open list (WEB-3271). Shared by
 * `WorkspaceContent` and `useActiveSubject` so the pane and the scope can't
 * disagree about what resolves. Lists may be undefined mid-flight; an id in
 * neither (an older completed visit) is unresolvable and falls back to the room.
 */
export const findBoardVisit = (
  visitUuid: string | null,
  ...lists: (TodayVisit[] | undefined)[]
): TodayVisit | null => {
  if (!visitUuid) return null;
  for (const list of lists) {
    const found = list?.find((visit) => visit.id === visitUuid);
    if (found) return found;
  }
  return null;
};

/**
 * Whether a visit sits on today's board. Drives whether a card shows its date
 * and whether "Late" applies, and in `useOpenVisit` whether the visit can be a
 * visit selection at all. Unscheduled counts as off-board.
 */
export const isOnTodayBoard = (scheduledStart: string | null): boolean =>
  scheduledStart ? isCurrentDay(new Date(scheduledStart)) : false;

/**
 * Whether a visit is running late: its scheduled start has passed but it's still
 * sitting on the first stage (position 0) without having moved along. Only
 * `active`/`pending` count — a `done`/`skipped` first stage means the visit has
 * progressed past it. Drives the subtle late cue on the patient-flow card.
 * First stage is `position === 0` — the backend orders stages from 0 (see the
 * position sort in toVisitDetail).
 */
export const isVisitLate = (visit: TodayVisit, now: number): boolean => {
  const stage = visit.currentStage;
  if (!visit.scheduledStart || !stage || stage.position !== 0) return false;
  if (stage.status !== "active" && stage.status !== "pending") return false;
  return new Date(visit.scheduledStart).getTime() < now;
};
